import styled from 'styled-components';

/* Design bands are max-width, not the theme's min-width set. */
const abp = (key: string) => (props: any) => props.theme.awarenessBreakpoints && props.theme.awarenessBreakpoints[key];

const HeroSection = styled.section`
    position: relative;
    width: 100%;
    aspect-ratio: 1440 / 323;
    overflow: hidden;
    background: #000;

    @media screen and (max-width: ${abp('w820')}) {
        aspect-ratio: auto;
        height: 140px;
    }

    @media screen and (max-width: ${abp('w600')}) {
        height: 146px;
    }
`;

const HeroMedia = styled.div<{ isMobileOnly?: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    /* Small screens show the mobile hero. Larger screens show the desktop hero. */
    display: ${props => (props.isMobileOnly ? 'none' : 'block')};

    @media screen and (max-width: ${abp('w600')}) {
        display: ${props => (props.isMobileOnly ? 'block' : 'none')};
    }

    picture, img {
        width: 100%;
        height: 100%;
    }

    /* The design mirrors its own stock photo; the CMS image is a different
       picture, so it keeps a crop that holds the faces. */
    img {
        object-fit: cover;
        object-position: center 12%;
    }
`;

const HeroInner = styled.div`
    position: relative;
    height: 100%;
    max-width: 1240px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    align-items: center;
`;

const HeroTitle = styled.h1`
    max-width: 400px;
    margin: 0;
    font-family: Anton;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.01em;
    color: #fff;
    font-size: 48px;
    line-height: 60px;

    @media screen and (max-width: ${abp('w820')}) {
        font-size: 40px;
        line-height: 46px;
    }

    @media screen and (max-width: ${abp('w600')}) {
        font-size: 32px;
        line-height: 38px;
        max-width: 260px;
    }
`;

// Dark full-width background. Inner content stays centred.
const PledgeSection = styled.section`
    background: #161719;
    color: #FCFCFE;
    padding: 56px 20px;
`;

const PledgeInner = styled.div`
    max-width: 1240px;
    margin: 0 auto;
`;

const PledgeHeading = styled.h2`
    margin: 0 0 32px;
    font-family: Anton;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.01em;
    color: #FCFCFE;

    && {
        font-size: 40px;
        line-height: 39px;
    }
`;

const PledgeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 64px;
    row-gap: 42px;
    align-items: start;

    @media screen and (max-width: ${abp('w900')}) {
        grid-template-columns: 1fr 1fr;
    }

    @media screen and (max-width: ${abp('w560')}) {
        grid-template-columns: 1fr;
    }
`;

const PledgeItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    min-width: 0;
`;

// 16:9, the ratio the supplied images are cropped to. Scales with the column
// rather than cropping top and bottom against a fixed height.
const PledgeImage = styled.div`
    width: 100%;

    a {
        display: block;
        width: 100%;
        height: 100%;
    }

    picture {
        display: block;
        position: relative;
        width: 100%;
        aspect-ratio: 16 / 9;
        overflow: hidden;
    }

    img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
`;

const PledgeCaption = styled.p`
    margin: 0;
    font-family: Overpass;
    font-weight: 300;
    color: #fff;
    font-size: 20px;
    line-height: 28px;
`;

// Sign up section. Copy on the left, form on the right.
const PledgeFormSection = styled.section`
    background: #FFFFFF;
    padding: 56px 20px 32px;

    @media screen and (max-width: ${abp('w600')}) {
        padding-top: 20px;
    }
`;

const PledgeFormInner = styled.div`
    max-width: 1240px;
    margin: 0 auto;
`;

const PledgeFormGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 36px;
    row-gap: 48px;
    align-items: start;

    @media screen and (max-width: ${abp('w820')}) {
        grid-template-columns: 1fr;
        gap: 28px;
    }
`;

const PledgeFormCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    p {
        margin: 0;
        font-family: Overpass;
        font-weight: 300;
        color: #000;
        font-size: 20px;
        line-height: 28px;
    }

    p:last-of-type {
        margin-bottom: 0;
    }
`;

const PledgeFormCopyHeading = styled.h2`
    margin: 0 0 16px;
    font-family: Anton;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.01em;
    color: #161719;
    font-size: 28px;
    line-height: 34px;

    @media screen and (min-width: ${props => props.theme.breakpoints && props.theme.breakpoints.md}) {
        font-size: 36px;
        line-height: 42px;
    }
`;

const PledgeFormCopyBody = styled.p`
    margin: 0;
    font-family: Overpass;
    font-weight: 300;
    color: #000;
    font-size: 20px;
    line-height: 28px;
`;

const PledgeFormReadMore = styled.a`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 2px;
    font-family: Overpass;
    font-weight: 300;
    font-size: 20px;
    line-height: 28px;
    color: #000;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
        text-underline-offset: 3px;
    }

    > svg {
        flex-shrink: 0;
    }
`;

// Recent signers row under the form. Hidden for now.
const RecentSigners = styled.div`
    margin-top: 32px;

    @media screen and (min-width: ${props => props.theme.breakpoints && props.theme.breakpoints.md}) {
        margin-top: 40px;
    }
`;

const RecentSignersLabel = styled.p`
    margin: 0 0 12px;
    font-family: Overpass;
    font-weight: 700;
    color: #161719;
    font-size: 16px;
    line-height: 20px;
`;

const RecentSignersRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const SignerPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 999px;
    background: #F2F2F4;
    font-family: Overpass;
    font-size: 14px;
    line-height: 18px;
    color: #161719;
    white-space: nowrap;
`;

const SignerName = styled.span`
    font-weight: 700;
`;

const SignerDot = styled.span`
    color: #8A8A93;
`;

const SignerTime = styled.span`
    font-weight: 400;
    color: #56565F;
`;

// Six Ways section. Shown after a successful sign up.
// Extra top space so the heading is not hidden under the site header.
const WaysSection = styled.section`
    background: #FFFFFF;
    padding: 56px 20px;
    scroll-margin-top: 100px;

    @media screen and (max-width: ${abp('w600')}) {
        padding: 56px 20px 40px;
    }
`;

const WaysInner = styled.div`
    max-width: 1240px;
    margin: 0 auto;
`;

const WaysEyebrow = styled.p`
    margin: 0 0 12px;
    font-family: Overpass;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: #5F6068;
    font-size: 16px;
    text-align: center;
`;

const WaysHeading = styled.h2`
    margin: 0 auto 40px;
    max-width: 640px;
    font-family: Anton;
    font-weight: 400;
    text-transform: uppercase;
    color: #161719;
    font-size: clamp(30px, 4vw, 48px);
    line-height: 1.06;
    text-align: center;
`;

const WaysGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 40px;
    row-gap: 44px;

    @media screen and (max-width: ${abp('w900')}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${abp('w600')}) {
        grid-template-columns: 1fr;
    }
`;

const WaysTile = styled.a`
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-decoration: none;
    color: inherit;
    cursor: pointer;

    &:focus-visible {
        outline: 3px solid #3A70D9;
        outline-offset: 3px;
    }
`;

const WaysTileImage = styled.div`
    width: 100%;

    picture {
        display: block;
        position: relative;
        width: 100%;
        aspect-ratio: 368 / 173;
        overflow: hidden;
    }

    picture img,
    picture source {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        filter: grayscale(1);
    }
`;

const WaysTileBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const WaysTileTitle = styled.h3`
    margin: 0;
    font-family: Overpass;
    font-weight: 600;
    text-transform: uppercase;
    color: #161719;
    font-size: 24px;
    line-height: 28px;
`;

const WaysTileBlurb = styled.p`
    margin: 0;
    font-family: Overpass;
    font-weight: 300;
    color: #56565F;
    font-size: 16px;
    line-height: 24px;
`;

const WaysTileCta = styled.span`
    display: block;
    font-family: Overpass;
    font-weight: 300;
    font-size: 20px;
    line-height: 28px;
    color: #161719;

    > svg {
        display: inline-block;
        vertical-align: middle;
        margin-left: 2px;
        width: 16px;
        height: 16px;
    }
`;

const OtherWaysSection = styled.section`
    background: #fff;
    padding: 56px 20px;
`;

const OtherWaysInner = styled.div`
    max-width: 1240px;
    margin: 0 auto;
`;

const OtherWaysHeading = styled.h2`
    margin: 0 0 24px;
    font-family: Overpass;
    font-weight: 300;
    font-size: 36px;
    line-height: 1.1;
    color: #161719;
`;

const OtherWaysButtons = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;

    a {
        min-width: 0;
        height: 48px;
        padding: 12px 24px;
        border: 0;
        border-radius: 24px;
        font-family: Overpass;
        font-weight: 600;
        font-size: 16px;
        line-height: 1;
        text-decoration: none;
        box-sizing: border-box;

        span {
            font-size: 16px;
            font-weight: 600;
            line-height: 1;
        }

        svg {
            display: none;
        }
    }

    a:first-child {
        background: #161719;
        color: #fff;

        &:hover,
        &:focus {
            background: #000;
            color: #fff;
        }
    }

    a:not(:first-child) {
        background: transparent;
        color: #161719;
        box-shadow: inset 0 0 0 1px rgba(22, 23, 25, 0.4);

        &:hover,
        &:focus {
            background: transparent;
            color: #161719;
        }
    }
`;

// Match 3 Awareness Raisers Landing: full-bleed black split, copy lined up with 1240px, portrait hidden under 821px.
const TransparencySection = styled.section<{ $hasMedia?: boolean }>`
    display: grid;
    grid-template-columns: 1fr;
    background: #161719;

    @media screen and (min-width: 821px) {
        grid-template-columns: ${props => (props.$hasMedia ? '1fr 1fr' : '1fr')};
    }
`;

const TransparencyCopy = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 56px clamp(24px, 5vw, 64px);
    padding-left: max(20px, calc((100vw - 1240px) / 2 + 20px));
    color: #fff;
`;

const TransparencyEyebrow = styled.p`
    margin: 0 0 12px;
    font-family: Overpass;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #B9BAC5;
`;

const TransparencyHeading = styled.h2`
    margin: 8px 0 24px;
    max-width: 640px;
    font-family: Anton;
    font-weight: 400;
    text-transform: uppercase;
    font-size: 40px;
    line-height: 1.06;
    text-align: left;
    color: #fff;
`;

const TransparencyBody = styled.div`
    margin: 0 0 32px;
    max-width: 460px;
    font-family: Overpass;
    font-weight: 300;
    font-size: 20px;
    line-height: 28px;
    color: #fff;

    p {
        margin: 0 0 12px;
        font-family: Overpass;
        font-weight: 300;
        font-size: 20px;
        line-height: 28px;
        color: #fff;
    }

    p:last-child {
        margin-bottom: 0;
    }

    a {
        color: inherit;
        text-decoration: underline;
    }
`;

const TransparencyCta = styled.div`
    align-self: flex-start;

    a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 0;
        height: 48px;
        padding: 12px 24px;
        border: 0;
        border-radius: 24px;
        background: #fff;
        color: #161719;
        font-family: Overpass;
        font-weight: 600;
        font-size: 16px;
        line-height: 1;
        text-decoration: none;
        box-sizing: border-box;
        transition: background 160ms cubic-bezier(0.2, 0.6, 0.2, 1);

        /* The shared Button renders its label in a span that carries its own
           size, so it has to be pinned here the way OtherWaysButtons does. */
        span {
            font-size: 16px;
            font-weight: 600;
            line-height: 1;
        }

        &:hover,
        &:focus {
            background: #F0F0F6;
            color: #161719;
        }

        svg {
            display: none;
        }
    }
`;

const TransparencyMedia = styled.div`
    position: relative;
    min-height: 420px;

    picture,
    img {
        display: block;
        width: 100%;
        height: 100%;
    }

    picture {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    img {
        object-fit: cover;
        object-position: center;
        filter: grayscale(1);
    }

    @media screen and (max-width: 820px) {
        display: none;
    }
`;

export {
    HeroSection,
    HeroMedia,
    HeroInner,
    HeroTitle,
    WaysSection,
    WaysInner,
    WaysEyebrow,
    WaysHeading,
    WaysGrid,
    WaysTile,
    WaysTileImage,
    WaysTileBody,
    WaysTileTitle,
    WaysTileBlurb,
    WaysTileCta,
    PledgeSection,
    PledgeInner,
    PledgeHeading,
    PledgeGrid,
    PledgeItem,
    PledgeImage,
    PledgeCaption,
    PledgeFormSection,
    PledgeFormInner,
    PledgeFormGrid,
    PledgeFormCopy,
    PledgeFormCopyHeading,
    PledgeFormCopyBody,
    PledgeFormReadMore,
    OtherWaysSection,
    OtherWaysInner,
    OtherWaysHeading,
    OtherWaysButtons,
    TransparencySection,
    TransparencyCopy,
    TransparencyEyebrow,
    TransparencyHeading,
    TransparencyBody,
    TransparencyCta,
    TransparencyMedia,
    RecentSigners,
    RecentSignersLabel,
    RecentSignersRow,
    SignerPill,
    SignerName,
    SignerDot,
    SignerTime,
};
